import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import logo from "@/assets/logo.webp";
import { socialLinks } from "@/components/layout/Footer";
import SearchDialog from "@/components/SearchDialog";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useRecipeFacetCounts } from "@/hooks/useRecipeFacetCounts";

type MenuLink = { label: string; to: string };
const recipeCategories: MenuLink[] = [
  { label: "Breakfast", to: "/recipes?category=breakfast" },
  { label: "Snacks", to: "/recipes?category=snacks" },
  { label: "Salads", to: "/recipes?category=salads" },
  { label: "Dinner", to: "/recipes?category=dinner" },
  { label: "Dessert", to: "/recipes?category=dessert" },
  { label: "Mixes/Spices", to: "/recipes?category=mixes_spices" },
];
const recipeMethods: MenuLink[] = [
  { label: "Instant Pot", to: "/recipe-index?method=instant-pot" },
  { label: "Slow Cooker", to: "/recipe-index?method=slow-cooker" },
  { label: "Air Fryer", to: "/recipe-index?method=air-fryer" },
  { label: "Grill", to: "/recipe-index?method=grill" },
  { label: "One Pot", to: "/recipe-index?method=one-pot" },
  { label: "No-Bake", to: "/recipe-index?method=no-bake" },
];
const recipeDiets: MenuLink[] = [
  { label: "Dairy-Free", to: "/recipe-index?diet=dairy-free" },
  { label: "Vegetarian", to: "/recipe-index?diet=vegetarian" },
  { label: "Low-Carb", to: "/recipe-index?diet=low-carb" },
  { label: "High Protein", to: "/recipe-index?diet=high-protein" },
  { label: "Kid-Friendly", to: "/recipe-index?diet=kid-friendly" },
];
const recipeHolidays: MenuLink[] = [
  { label: "Christmas", to: "/recipe-index?holiday=christmas" },
  { label: "Thanksgiving", to: "/recipe-index?holiday=thanksgiving" },
  { label: "Easter", to: "/recipe-index?holiday=easter" },
  { label: "4th of July", to: "/recipe-index?holiday=4th-of-july" },
  { label: "Halloween", to: "/recipe-index?holiday=halloween" },
  { label: "Game Day", to: "/recipe-index?holiday=game-day" },
];

type MegaSection = { title: string; links: MenuLink[] };
const recipeMega: MegaSection[] = [
  { title: "Categories", links: recipeCategories },
  { title: "Methods", links: recipeMethods },
  { title: "Diet", links: recipeDiets },
  { title: "Holidays", links: recipeHolidays },
];

const allNavLinks: Array<{
  label: string;
  to: string;
  dropdown?: MenuLink[];
  mega?: MegaSection[];
}> = [
  { label: "Home", to: "/" },
  { label: "About Me", to: "/about" },
  { label: "Recipes", to: "/recipes", mega: recipeMega },
  { label: "Cookbooks", to: "/cookbooks" },
  { label: "Blog", to: "/blog" },
  { label: "Shop", to: "/shop" },
  { label: "Events", to: "/events" },
  { label: "Press", to: "/press" },
  
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const location = useLocation();
  const { data: settings } = useSiteSettings();
  const shopVisible = settings?.shop_visible !== false;
  const navLinks = allNavLinks.filter((l) => l.to !== "/shop" || shopVisible);
  const { data: facetCounts } = useRecipeFacetCounts();

  const linkHasResults = (to: string): boolean => {
    if (!facetCounts) return true;
    const m = to.match(/[?&](method|diet|holiday|category)=([^&]+)/);
    if (!m) return true;
    const key = m[1] as "method" | "diet" | "holiday" | "category";
    const value = decodeURIComponent(m[2]);
    return (facetCounts[key]?.[value] ?? 0) > 0;
  };
  const filteredMega = (mega?: MegaSection[]) =>
    mega
      ?.map((s) => ({ ...s, links: s.links.filter((l) => linkHasResults(l.to)) }))
      .filter((s) => s.links.length > 0);

  return (
    <>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <nav className="lg:sticky lg:top-0 z-50 bg-[hsl(var(--nav-bg))]" role="banner" aria-label="Main navigation">
        <div className="container flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Of Course It's Good" className="h-10 md:h-12 object-contain" width={170} height={48} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                <Link
                  to={link.to}
                  className={`px-3 py-2 text-sm font-heading uppercase tracking-wide transition-colors hover:text-primary ${
                    location.pathname === link.to ? "text-primary" : "text-[hsl(var(--nav-foreground))]/80"
                  }`}
                >
                  {link.label}
                  {(link.dropdown || link.mega) && <ChevronDown className="inline ml-1 h-3 w-3" />}
                </Link>
                {link.dropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[180px]">
                    {link.dropdown.map((cat) => (
                      <Link
                        key={cat.label}
                        to={cat.to}
                        className="block px-4 py-2.5 text-sm hover:bg-accent hover:text-foreground transition-colors font-body first:rounded-t-lg last:rounded-b-lg"
                      >
                        {cat.label}
                      </Link>
                    ))}
                  </div>
                )}
                {link.mega && (() => { const mega = filteredMega(link.mega); return mega && mega.length > 0 && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-card border border-border rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-[720px] p-5 z-50">
                    <div className={`grid gap-5`} style={{ gridTemplateColumns: `repeat(${mega.length}, minmax(0, 1fr))` }}>
                      {mega.map((section) => (
                        <div key={section.title}>
                          <p className="font-heading text-xs font-bold uppercase tracking-wider text-primary mb-2">
                            {section.title}
                          </p>
                          <ul className="space-y-1.5">
                            {section.links.map((l) => (
                              <li key={l.label}>
                                <Link
                                  to={l.to}
                                  className="block text-sm font-body text-foreground/80 hover:text-primary transition-colors"
                                >
                                  {l.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                      <Link to="/recipe-index" className="font-heading uppercase tracking-wide text-primary hover:underline">
                        Browse the Full Recipe Index →
                      </Link>
                      <Link to="/recipe-request" className="text-muted-foreground hover:text-primary font-body">
                        Request a Recipe
                      </Link>
                    </div>
                  </div>
                ); })()}
              </div>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-[hsl(var(--nav-foreground))]/70 hover:text-primary hover:bg-transparent"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
            <a href="https://merch.ofcourseitsgood.com" target="_blank" rel="noopener noreferrer" className="hidden md:block">
              <Button size="sm" variant="outline" className="font-heading uppercase tracking-wide text-xs border-primary text-foreground hover:bg-primary hover:text-primary-foreground rounded-sm">
                Shop Merch
              </Button>
            </a>

            {/* Mobile Menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="text-[hsl(var(--nav-foreground))]" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-card border-border w-[300px] p-0 flex flex-col">
                <div className="px-5 pt-5 pb-3 border-b border-border">
                  <SheetTitle className="font-heading text-primary uppercase tracking-wider text-lg">Menu</SheetTitle>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setSearchOpen(true); }}
                    className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-full border border-border bg-background text-sm text-muted-foreground font-body hover:text-primary hover:border-primary transition-colors"
                  >
                    <Search className="h-4 w-4" />
                    Search recipes...
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-3">
                  {(() => {
                    const groups: Array<{ heading: string; labels: string[] }> = [
                      { heading: "Explore", labels: ["Home", "Recipes", "Blog"] },
                      { heading: "Discover", labels: ["Cookbooks", "Shop", "Events", "Press"] },
                      { heading: "Connect", labels: ["About Me", "Contact"] },
                    ];
                    return groups.map((group) => {
                      const items = group.labels
                        .map((l) => navLinks.find((n) => n.label === l))
                        .filter(Boolean) as typeof navLinks;
                      if (items.length === 0) return null;
                      return (
                        <div key={group.heading} className="mb-4">
                          <p className="px-3 mb-1 font-heading text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/70">
                            {group.heading}
                          </p>
                          <div className="flex flex-col gap-0.5">
                            {items.map((link) => {
                              const mega = filteredMega(link.mega);
                              const hasSub = !!link.dropdown || (mega && mega.length > 0);
                              const isOpen = expanded === link.label;
                              return (
                                <div key={link.label}>
                                  {hasSub ? (
                                    <div className="flex items-center">
                                      <Link
                                        to={link.to}
                                        onClick={() => setOpen(false)}
                                        className={`flex-1 block px-3 py-2.5 font-heading uppercase tracking-wide text-[15px] transition-colors rounded-lg hover:bg-accent ${
                                          location.pathname === link.to ? "text-primary bg-accent" : "text-foreground/85"
                                        }`}
                                      >
                                        {link.label}
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={() => setExpanded((e) => (e === link.label ? null : link.label))}
                                        aria-label={`Toggle ${link.label} submenu`}
                                        aria-expanded={isOpen}
                                        className="p-2 mr-1 rounded-lg text-foreground/60 hover:text-primary hover:bg-accent"
                                      >
                                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                      </button>
                                    </div>
                                  ) : (
                                    <Link
                                      to={link.to}
                                      onClick={() => setOpen(false)}
                                      className={`block px-3 py-2.5 font-heading uppercase tracking-wide text-[15px] transition-colors rounded-lg hover:bg-accent ${
                                        location.pathname === link.to ? "text-primary bg-accent" : "text-foreground/85"
                                      }`}
                                    >
                                      {link.label}
                                    </Link>
                                  )}
                                  {link.dropdown && isOpen && (
                                    <div className="pl-4 mt-1 mb-2 flex flex-col border-l border-border ml-4">
                                      {link.dropdown.map((cat) => (
                                        <Link
                                          key={cat.label}
                                          to={cat.to}
                                          onClick={() => setOpen(false)}
                                          className="block px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors font-body"
                                        >
                                          {cat.label}
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                  {mega && isOpen && (
                                    <div className="pl-4 mt-1 mb-2 ml-4 border-l border-border">
                                      {mega.map((section) => (
                                        <div key={section.title} className="mb-2">
                                          <p className="px-3 pt-1 pb-0.5 font-heading text-[10px] font-bold uppercase tracking-[0.12em] text-primary/80">
                                            {section.title}
                                          </p>
                                          <div className="flex flex-col">
                                            {section.links.map((cat) => (
                                              <Link
                                                key={`${cat.label}-${cat.to}`}
                                                to={cat.to}
                                                onClick={() => setOpen(false)}
                                                className="block px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors font-body"
                                              >
                                                {cat.label}
                                              </Link>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                      <Link
                                        to="/recipe-index"
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2 mt-1 text-xs text-primary font-heading uppercase tracking-wide"
                                      >
                                        Full Recipe Index →
                                      </Link>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="border-t border-border px-4 py-3">
                  <a href="https://merch.ofcourseitsgood.com" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="w-full font-heading uppercase tracking-wide rounded-sm">
                      Shop Merch
                    </Button>
                  </a>
                  <div className="flex items-center gap-2 justify-center mt-3">
                    {socialLinks.map((s) => (
                      <a
                        key={s.label}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        className="w-8 h-8 rounded-full bg-accent flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-foreground/60"
                      >
                        <s.icon className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
